// Display-only special-price on Rebuy Smart Cart cross-sell cards ("Customers also added").
//
// Rebuy renders those cards (Vue), not the theme's Liquid, and its product feed does NOT carry the
// `custom.special_price_ui` metafield — so we post-process the rendered DOM. For each card we read
// its handle + variant id from the product link, fetch the variant's price + special from the
// theme's own data endpoint (templates/product.discount.liquid, `?view=discount`), and when a
// special is set below the regular price we rewrite `.rebuy-product-price` to show the special as
// the selling price with the original struck through. Display-only: nothing here touches the cart
// or checkout price (mirrors the PDP frequently-bought UI-discount).

import { fetchVariantSpecials } from './special-price-lookup.js';

// Single-currency USD store — cards render Shopify's default `$X.XX`. Values are integer cents.
// ponytail: hardcoded '$', swap to a Shopify money-format pass if the store ever goes multi-currency.
const formatMoney = (cents) => `$${(cents / 100).toFixed(2)}`;

const parseLink = (card) => {
    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    const url = new URL(link.href, window.location.origin);
    const match = url.pathname.match(/\/products\/([^/?#]+)/);
    const variantId = url.searchParams.get('variant');
    if (!match || !variantId) return null;
    return { handle: match[1], variantId };
};

const applyDiscount = async (card) => {
    const priceEl = card.querySelector('.rebuy-product-price');
    // Guard makes this idempotent: our own rewrite sets the flag, so the MutationObserver callback
    // triggered BY that rewrite skips the card. A later Rebuy re-render replaces the node (flag gone
    // with it), so the fresh card is reprocessed cleanly — no loop, no double-injection.
    if (!priceEl || priceEl.dataset.uiDiscount) return;

    const link = parseLink(card);
    if (!link) return;

    const variants = await fetchVariantSpecials(link.handle);
    const entry = variants[link.variantId];
    if (!entry) return;

    const { price, special } = entry;
    if (!(special > 0 && special < price)) return;

    priceEl.dataset.uiDiscount = '1';
    priceEl.classList.add('rebuy-product-price--special');
    priceEl.innerHTML =
        `<span class="rebuy-money ui-special-price__now">${formatMoney(special)}</span>` +
        `<s class="ui-special-price__was">${formatMoney(price)}</s>`;
};

const scan = (root) => {
    root.querySelectorAll('.rebuy-product-block').forEach(applyDiscount);
};

export const initRebuyUiDiscount = () => {
    // Rebuy fires a burst of DOM mutations per render; coalesce into one scan per frame so we don't
    // run querySelectorAll on the whole body per mutation.
    let scheduled = false;
    const scheduleScan = () => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
            scheduled = false;
            scan(document.body);
        });
    };

    // Cards mount asynchronously and Rebuy re-renders them on cart changes, so watch the whole
    // document subtree and rescan on Rebuy's cart signals (same events rebuy-cart-sync.js uses).
    new MutationObserver(scheduleScan).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('rebuy:cart.change', scheduleScan);
    document.addEventListener('rebuy:cart.ready', scheduleScan);

    scan(document.body);
};

export default initRebuyUiDiscount;
